/**
 * TierAccessService.ts
 * Centralized 4-tier access helper, custom HTTP denial header parser, and accessible
 * subscription/account callout renderer for SkySkout Web UI trend charts and map controls.
 *
 * SkySkout Image / Tile Access Tiers (free < registered < pro < ultra):
 *   * Free - Unregistered users in the web ui or app
 *   * Registered - User that have made an account and are authenticated against firebase but are unpaid.
 *   * Pro - Users who have paid.
 *   * Ultra - for future use as a tier above pro.
 */

import store, { AppState, parseSessionTokenClaims } from '../state';
import {
  AccessTier,
  TIER_RANK,
  TIER_LABELS,
  SUBSCRIPTION_DENIAL_CONFIG,
  LAYER_TIERS,
  BASEMAP_THEME_TIERS,
  getRequiredTierForLayer,
  getRequiredTierForBasemapTheme,
  hasTierAccess,
} from '../config/layer_tiers.generated';

export {
  TIER_RANK,
  TIER_LABELS,
  SUBSCRIPTION_DENIAL_CONFIG,
  LAYER_TIERS,
  BASEMAP_THEME_TIERS,
  getRequiredTierForLayer,
  getRequiredTierForBasemapTheme,
  hasTierAccess,
};
export type { AccessTier };

export interface TileAccessDenial {
  status: number;
  code: 'AUTH_REQUIRED' | 'SUBSCRIPTION_REQUIRED' | string;
  requiredTier: AccessTier;
  userTier: AccessTier;
  message: string;
  url?: string;
  timestamp: number;
}

export interface TrendChartDenialRenderOptions {
  denial?: TileAccessDenial | null;
  requiredTier?: AccessTier;
  featureLabel?: string;
  compact?: boolean;
}

function isValidTier(value: unknown): value is AccessTier {
  return value === 'free' || value === 'registered' || value === 'pro' || value === 'ultra';
}

export class TierAccessService {
  private static listenerInstalled = false;

  public static ensureDelegatedListeners(): void {
    if (this.listenerInstalled || typeof document === 'undefined') return;
    this.listenerInstalled = true;

    document.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const ctaBtn = target?.closest?.('[data-skyskout-tier-cta]') as HTMLElement | null;
      if (!ctaBtn) return;

      e.preventDefault();
      e.stopPropagation();
      const action = (ctaBtn.getAttribute('data-skyskout-tier-cta') || 'pro').toLowerCase();
      if (action === 'signin') {
        const modal = (window as any).__authModal;
        if (modal && typeof modal.open === 'function') {
          modal.open('signin');
        } else {
          store.update({ isAuthModalOpen: true });
        }
      } else if (action === 'register' || action === 'registered') {
        this.openTierUpgradeAction('registered');
      } else if (action === 'ultra') {
        this.openTierUpgradeAction('ultra');
      } else {
        this.openTierUpgradeAction('pro');
      }
    });
  }

  /**
   * Resolves the current user's effective access tier from application state:
   * - Free: Unregistered users in the web ui or app
   * - Registered: User that have made an account and are authenticated against firebase but are unpaid.
   * - Pro: Users who have paid.
   * - Ultra: for future use as a tier above pro.
   */
  public static getUserAccessTier(state: AppState = store.getState()): AccessTier {
    const sub = state?.subscription as (AppState['subscription'] & { tier?: AccessTier }) | undefined;
    if (sub && isValidTier(sub.tier)) {
      if (sub.tier === 'ultra') return 'ultra';
      if (sub.tier === 'pro') return 'pro';
    }

    const plan = (sub?.plan || '').toLowerCase();
    const hasPaidAccess = Boolean(
      sub && (
        sub.hasAccess ||
        sub.status === 'active' ||
        sub.status === 'trialing' ||
        sub.status === 'comped' ||
        plan.startsWith('comped')
      )
    );

    if (hasPaidAccess) {
      if (plan.includes('ultra')) return 'ultra';
      return 'pro';
    }

    const tokenClaims = parseSessionTokenClaims(state?.sessionToken);
    if (tokenClaims && isValidTier(tokenClaims.tier)) {
      if (tokenClaims.tier === 'ultra' || tokenClaims.tier === 'pro' || tokenClaims.tier === 'registered') {
        return tokenClaims.tier;
      }
    }

    if (state?.isAuthenticated) {
      return 'registered';
    }

    return 'free';
  }

  public static userHasLayerAccess(layerId: string | null | undefined, state: AppState = store.getState()): boolean {
    const userTier = this.getUserAccessTier(state);
    const reqTier = getRequiredTierForLayer(layerId);
    return hasTierAccess(userTier, reqTier);
  }

  public static userHasBasemapAccess(
    theme: string | null | undefined,
    mountainMode?: string,
    state: AppState = store.getState()
  ): boolean {
    const userTier = this.getUserAccessTier(state);
    const reqTier = getRequiredTierForBasemapTheme(theme, mountainMode);
    return hasTierAccess(userTier, reqTier);
  }

  /**
   * Parses custom SkySkout Gatekeeper HTTP 401/402/403 subscription & tier denial responses.
   */
  public static parseTileAccessDenial(
    resp: Response,
    fallbackRequiredTier: AccessTier = 'pro'
  ): TileAccessDenial | null {
    if (!resp || (resp.status !== 401 && resp.status !== 402 && resp.status !== 403)) {
      return null;
    }

    const headerCode = resp.headers?.get?.(SUBSCRIPTION_DENIAL_CONFIG.headerDeniedReason)?.trim() || '';
    const rawReqTier = resp.headers?.get?.(SUBSCRIPTION_DENIAL_CONFIG.headerRequiredTier)?.trim().toLowerCase() || '';
    const rawUserTier = resp.headers?.get?.(SUBSCRIPTION_DENIAL_CONFIG.headerUserTier)?.trim().toLowerCase() || '';

    let requiredTier: AccessTier = fallbackRequiredTier;
    if (isValidTier(rawReqTier)) {
      requiredTier = rawReqTier;
    } else if (resp.status === 402 && TIER_RANK[fallbackRequiredTier] < TIER_RANK.pro) {
      requiredTier = 'pro';
    }

    const userTier: AccessTier = isValidTier(rawUserTier)
      ? rawUserTier
      : this.getUserAccessTier();

    const code = headerCode || (
      TIER_RANK[requiredTier] >= TIER_RANK.pro
        ? SUBSCRIPTION_DENIAL_CONFIG.insufficientTierCode
        : SUBSCRIPTION_DENIAL_CONFIG.unauthenticatedCode
    );

    const tierDisplay = requiredTier === 'ultra'
      ? 'SkySkout Ultra'
      : requiredTier === 'pro'
        ? 'SkySkout Pro'
        : 'a free SkySkout account';

    const message = TIER_RANK[requiredTier] >= TIER_RANK.pro
      ? `Viewing this data requires a ${tierDisplay} subscription.`
      : 'Sign in or create a free SkySkout account to view this data.';

    return {
      status: resp.status,
      code,
      requiredTier,
      userTier,
      message,
      url: resp.url || undefined,
      timestamp: Date.now(),
    };
  }

  public static openTierUpgradeAction(requiredTier: AccessTier = 'pro'): void {
    const state = store.getState();
    const modal = (window as any).__authModal;

    if (requiredTier === 'registered' && !state.isAuthenticated) {
      if (modal && typeof modal.open === 'function') {
        modal.open('register');
      } else {
        store.update({ isAuthModalOpen: true });
      }
      return;
    }

    if (modal && typeof modal.open === 'function') {
      modal.open(state.isAuthenticated ? 'subscribe' : 'subscribe');
      return;
    }

    if (state.isAuthenticated) {
      const panel = (window as any).__accountPanel;
      if (panel && typeof panel.open === 'function') {
        panel.open('account');
        return;
      }
    }
    store.update({ isAuthModalOpen: true });
  }

  public static getTierBadgeLabel(requiredTier: AccessTier): string {
    if (requiredTier === 'ultra') return 'ULTRA';
    if (requiredTier === 'pro') return 'PRO';
    if (requiredTier === 'registered') return 'FREE ACCT';
    return 'FREE';
  }

  /**
   * Builds an accessible, high-contrast (>= 4.5:1 text contrast, explicit 1px solid border for
   * Forced Colors Mode, paired iconography + text label) trend chart denial callout.
   */
  public static buildTrendChartDenialHtml(options: TrendChartDenialRenderOptions = {}): string {
    this.ensureDelegatedListeners();

    const state = store.getState();
    const userTier = options.denial?.userTier || this.getUserAccessTier(state);
    const requiredTier = options.denial?.requiredTier || options.requiredTier || 'pro';
    const featureLabel = options.featureLabel || 'forecast trend charts';
    const compact = Boolean(options.compact);

    const isPaidTier = TIER_RANK[requiredTier] >= TIER_RANK.pro;
    const isUltra = requiredTier === 'ultra';

    const title = isUltra
      ? 'SkySkout Ultra Subscription Required'
      : isPaidTier
        ? 'SkySkout Pro Subscription Required'
        : 'Free SkySkout Account Required';

    const badgeText = this.getTierBadgeLabel(requiredTier);

    const description = isUltra
      ? `Upgrade to SkySkout Ultra to unlock ${featureLabel}.`
      : isPaidTier
        ? `Upgrade to SkySkout Pro to view ${featureLabel}.`
        : `Sign in or create a free account to view ${featureLabel}.`;

    const primaryCtaAction = isUltra ? 'ultra' : (isPaidTier ? 'pro' : 'register');
    const primaryCtaText = isUltra
      ? 'Explore Ultra Plans'
      : isPaidTier
        ? (state.isAuthenticated ? 'Upgrade to SkySkout Pro' : 'Start Free Trial / Subscribe')
        : 'Create Free Account';

    const showSecondarySignIn = !state.isAuthenticated && userTier === 'free';

    const borderColor = isPaidTier ? 'rgba(251, 191, 36, 0.55)' : 'rgba(56, 189, 248, 0.55)';
    const accentText = isPaidTier ? '#fde68a' : '#bae6fd';
    const badgeBg = isPaidTier ? 'rgba(245, 158, 11, 0.22)' : 'rgba(14, 165, 233, 0.22)';
    const ctaBg = isPaidTier ? '#fbbf24' : '#38bdf8';
    const ctaFg = '#0f172a';

    const lockSvg = `<svg width="${compact ? 13 : 15}" height="${compact ? 13 : 15}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>`;

    if (compact) {
      return `
        <div role="region" aria-label="${title}" style="display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 6px 10px; width: 100%; padding: 6px 10px; background: rgba(15, 23, 42, 0.92); border: 1px solid ${borderColor}; border-radius: 6px; box-sizing: border-box; text-align: left;">
          <div style="display: inline-flex; align-items: center; gap: 6px; color: ${accentText}; font-weight: 700; font-size: 0.74rem; line-height: 1.2;">
            ${lockSvg}
            <span>${title}</span>
            <span style="font-size: 0.60rem; font-weight: 800; letter-spacing: 0.4px; padding: 1px 5px; border-radius: 4px; background: ${badgeBg}; border: 1px solid ${borderColor}; color: #f8fafc;">${badgeText}</span>
          </div>
          <span style="font-size: 0.69rem; color: #e2e8f0; line-height: 1.25;">${description}</span>
          <div style="display: inline-flex; align-items: center; gap: 6px;">
            <button type="button" data-skyskout-tier-cta="${primaryCtaAction}" style="padding: 3px 9px; background: ${ctaBg}; color: ${ctaFg}; border: 1px solid ${ctaBg}; border-radius: 4px; font-size: 0.68rem; font-weight: 800; cursor: pointer; line-height: 1.25;">
              ${primaryCtaText}
            </button>
            ${showSecondarySignIn ? `
              <button type="button" data-skyskout-tier-cta="signin" style="padding: 3px 8px; background: transparent; color: #f8fafc; border: 1px solid rgba(203, 213, 225, 0.5); border-radius: 4px; font-size: 0.68rem; font-weight: 700; cursor: pointer; line-height: 1.25;">
                Sign In
              </button>
            ` : ''}
          </div>
        </div>
      `;
    }

    return `
      <div role="region" aria-label="${title}" style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 110px; height: 100%; padding: 14px 16px; background: rgba(15, 23, 42, 0.92); border: 1px solid ${borderColor}; border-radius: 10px; box-sizing: border-box; text-align: center;">
        <div style="display: inline-flex; align-items: center; gap: 7px; color: ${accentText}; font-weight: 800; font-size: 0.86rem; line-height: 1.25;">
          ${lockSvg}
          <span>${title}</span>
          <span style="font-size: 0.62rem; font-weight: 800; letter-spacing: 0.5px; padding: 2px 6px; border-radius: 4px; background: ${badgeBg}; border: 1px solid ${borderColor}; color: #f8fafc;">${badgeText}</span>
        </div>
        <p style="margin: 0; max-width: 400px; font-size: 0.76rem; color: #e2e8f0; line-height: 1.38;">
          ${description}
        </p>
        <div style="display: inline-flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 8px; margin-top: 2px;">
          <button type="button" data-skyskout-tier-cta="${primaryCtaAction}" style="padding: 6px 14px; background: ${ctaBg}; color: ${ctaFg}; border: 1px solid ${ctaBg}; border-radius: 6px; font-size: 0.74rem; font-weight: 800; cursor: pointer; line-height: 1.25;">
            ${primaryCtaText}
          </button>
          ${showSecondarySignIn ? `
            <button type="button" data-skyskout-tier-cta="signin" style="padding: 6px 12px; background: rgba(30, 41, 59, 0.85); color: #f8fafc; border: 1px solid rgba(203, 213, 225, 0.55); border-radius: 6px; font-size: 0.74rem; font-weight: 700; cursor: pointer; line-height: 1.25;">
              Sign In
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }
}

TierAccessService.ensureDelegatedListeners();

export default TierAccessService;
